/**
 * Человекочитаемый текст ошибки бэкенда — один на весь фронт (AK-574).
 *
 * Раньше эта функция жила приватно в `VarietiesPage.tsx`, а половина
 * справочников вообще не ловила отказ: `await mutateAsync(...)` без `catch`
 * даёт unhandled rejection — 409 «занято» и 422 «неверное значение» уходили в
 * консоль, форма оставалась открытой, и пользователь читал это как «сохранил».
 *
 * Разворачиваем три уровня:
 *   1. `rawDetail.usage` — 409 usage-гейта справочников: показываем, ГДЕ занято
 *      («посевы: 12, техкарты: 3»), иначе «нельзя удалить» звучит как каприз;
 *   2. `rawDetail.detail` — текст FastAPI HTTPException;
 *   3. `message` — сетевой сбой и всё остальное.
 */
import { ApiError } from './api';
import { captureException } from './sentry';

export function describeApiError(err: unknown): string {
  const e = err as {
    rawDetail?: { detail?: string; usage?: Record<string, number> };
    detail?: string;
    message?: string;
  };
  const usage = e?.rawDetail?.usage;
  if (usage && Object.keys(usage).length > 0) {
    const parts = Object.entries(usage).map(([k, n]) => `${k}: ${n}`);
    return `${e.rawDetail?.detail ?? 'Запись используется'} (${parts.join(', ')})`;
  }
  return (
    e?.rawDetail?.detail ??
    e?.detail ??
    e?.message ??
    'Не удалось выполнить операцию'
  );
}

/**
 * Разбор ошибки ЗАГРУЗКИ для экрана, а не для тоста (AK-790 re-land #670,
 * ревизия паттерна по вердикту head-web): в каждой карточке дашборда жила
 * своя копия одного catch'а, и все они глотали 500 в Sentry, рисуя «Нет
 * данных». Здесь решение одно на всех:
 *   · 5xx и «нет сети» (status 0) — СЕРВЕРНАЯ ошибка: пользователю не помочь
 *     нашим `detail`, показываем нейтральное «сервер отвечает ошибкой» и
 *     отправляем в Sentry;
 *   · 4xx — штатный ответ бэка (нет прав, нет компании и т.п.): показываем
 *     человекочитаемый `detail` через `describeApiError`, Sentry не тревожим.
 *
 * `describeApiError` выше не трогаем — у него другой контракт (строка для
 * тоста, AK-574) и полтора десятка потребителей.
 */
export interface ClassifiedApiError {
  /** Готовый к показу текст. */
  message: string;
  /** true — 5xx или сеть: детали серверу не помогут, показана нейтралка. */
  isServer: boolean;
  /** HTTP status; 0 — сетевой сбой или не-HTTP исключение. */
  status: number;
}

export const SERVER_ERROR_MESSAGE = 'Сервер отвечает ошибкой. Попробуйте обновить.';

/**
 * @param err — что прилетело в catch
 * @param source — ярлык места для Sentry-тега / dev-лога. Пользователю не показывается.
 */
export function classifyApiError(err: unknown, source: string): ClassifiedApiError {
  const isApi = err instanceof ApiError;
  const status = isApi ? err.status : 0;
  const isServer = status >= 500 || status === 0;

  if (isServer) {
    if (import.meta.env.DEV) console.error(`[${source}] ${status || 'network'}`, err);
    else captureException(err, { tags: { source } });
    return { message: SERVER_ERROR_MESSAGE, isServer: true, status };
  }

  if (import.meta.env.DEV) console.error(`[${source}] ${status}`, err);
  return { message: describeApiError(err), isServer: false, status };
}
